import {
    AlertTriangle,
    ArrowRight,
    ArrowRightLeft,
    Bot,
    CalendarClock,
    CheckCircle2,
    ChevronDown,
    CircleDot,
    Clock,
    Copy,
    CornerDownRight,
    CornerUpLeft,
    Cpu,
    Edit3,
    FastForward,
    Filter,
    Flag,
    GitBranch,
    HelpCircle,
    LogIn,
    LucideIcon,
    MailCheck,
    MessageSquare,
    Moon,
    PhoneCall,
    PhoneOff,
    Play,
    RefreshCw,
    ShieldOff,
    SkipForward,
    Terminal,
    User
} from "lucide-react";
import { SimpleTooltip } from "@/components/ui/simple-tooltip";
import { cn } from "@/lib/utils";
import { TimelineDetail, TimelineEntry } from "@/types/dialer/timeline";
import { formatParis, humanizeToken, utcTooltip } from "@/utils/dialer-datetime";
import { categoryVisual } from "@/components/dialer/graph/category-visuals";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

export const DEFAULT_TIMELINE_ICON: LucideIcon = CircleDot;

/** Server-provided icon names, kebab-case. An unknown one falls back to a neutral dot. */
const TIMELINE_ICONS: Record<string, LucideIcon> = {
    "phone-call": PhoneCall,
    "phone-off": PhoneOff,
    "message-square": MessageSquare,
    "mail-check": MailCheck,
    "git-branch": GitBranch,
    "arrow-right": ArrowRight,
    "calendar-clock": CalendarClock,
    clock: Clock,
    "shield-off": ShieldOff,
    moon: Moon,
    play: Play,
    flag: Flag,
    "log-in": LogIn,
    "corner-down-right": CornerDownRight,
    "corner-up-left": CornerUpLeft,
    "alert-triangle": AlertTriangle,
    "check-circle": CheckCircle2,
    "edit-3": Edit3,
    "refresh-cw": RefreshCw,
    "skip-forward": SkipForward,
    "fast-forward": FastForward,
    filter: Filter,
    copy: Copy,
    "help-circle": HelpCircle,
    bot: Bot,
    cpu: Cpu,
    terminal: Terminal,
    "arrow-right-left": ArrowRightLeft,
    user: User
};

export function timelineIcon(name: string | null | undefined): LucideIcon {
    if (!name) return DEFAULT_TIMELINE_ICON;
    return TIMELINE_ICONS[name] ?? DEFAULT_TIMELINE_ICON;
}

/**
 * The mandatory fallback. A projected entry carries a `title`; an event type the
 * projector does not know yet carries none, and rather than render an empty line we
 * humanise the raw `type`. Less pretty, never broken.
 */
export function entryTitle(entry: TimelineEntry): string {
    if (entry.title && entry.title.trim()) return entry.title;
    return humanizeToken(entry.type) || "Unknown event";
}

function stringifyPayloadValue(value: unknown): string {
    if (value === null || value === undefined) return "—";
    if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") return String(value);
    return JSON.stringify(value);
}

/**
 * Details, falling back to the payload keys when the server sent none. Same reason as
 * `entryTitle`: an unprojected event must still say something useful for diagnosis.
 */
export function entryDetails(entry: TimelineEntry): TimelineDetail[] {
    if (entry.details && entry.details.length > 0) return entry.details;
    const payload = entry.payload;
    if (!payload || typeof payload !== "object") return [];
    return Object.keys(payload).map(key => ({ label: humanizeToken(key), value: stringifyPayloadValue(payload[key]) }));
}

/** Internal identifiers and routing bookkeeping do not help the default diagnosis. */
const TECHNICAL_DETAIL_LABELS = new Set([
    "Call sid",
    "Conversation id",
    "Provider call id",
    "Port",
    "To",
    "On deadline",
    "Call depth",
    "To version",
    "From version",
    "Handover number",
    "From node id",
    "Resume node id",
    "Return to node id"
]);

function displayEntryDetails(entry: TimelineEntry): TimelineDetail[] {
    return entryDetails(entry).filter(detail => !TECHNICAL_DETAIL_LABELS.has(detail.label));
}

export function isEngineEntry(entry: TimelineEntry): boolean {
    // An entry with no `kind` is treated as engine noise: that is what an unprojected
    // event looks like, and the business view must stay readable by default.
    return (entry.kind ?? "engine") !== "business";
}

type TimelineItemProps = {
    entry: TimelineEntry;
    category?: string;
    selected?: boolean;
    onSelect?: (entry: TimelineEntry) => void;
    /**
     * Soft insert when the entry just arrived at the top of the journal (auto-refresh
     * or "Load newer"). Height expands via grid-rows so the list does not jump.
     */
    entering?: boolean;
};

export function TimelineItem({ entry, category, selected, onSelect, entering = false }: TimelineItemProps) {
    // An event that ends the card abnormally (it left the campaign) opens on its facts: they say why.
    const danger = entry.tone === "danger";
    const [expanded, setExpanded] = useState(danger);
    // Start collapsed when entering so the first paint does not claim full height;
    // expand on the next frame so the CSS transition has a 0fr → 1fr edge to run.
    const [rowExpanded, setRowExpanded] = useState(!entering);
    const Icon = timelineIcon(entry.icon);
    const details = displayEntryDetails(entry);
    const engine = isEngineEntry(entry);
    const visual = categoryVisual(category);
    const CategoryIcon = visual.icon;

    useEffect(() => {
        if (!entering) {
            setRowExpanded(true);
            return;
        }
        setRowExpanded(false);
        const frame = requestAnimationFrame(() => {
            setRowExpanded(true);
        });
        return () => cancelAnimationFrame(frame);
    }, [entering]);

    return (
        <li
            data-testid={`timeline-entry-${entry.sequence}`}
            data-entering={entering ? "true" : undefined}
            className={cn(
                "ease-[cubic-bezier(0.22,1,0.36,1)] grid transition-[grid-template-rows] duration-500",
                rowExpanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
                // Let the browser keep the already-visible rows stable while this one grows in.
                entering && "[overflow-anchor:none]"
            )}
        >
            <div className="min-h-0 overflow-hidden">
                <div
                    onClick={() => onSelect?.(entry)}
                    className={cn(
                        "mb-1 flex gap-3 rounded-md border border-l-4 bg-white px-3 py-2 transition-colors",
                        engine ? "border-l-gray-300" : visual.border,
                        danger && "border-red-200 border-l-red-600 bg-red-50",
                        selected && "ring-2 ring-primary",
                        onSelect && "cursor-pointer hover:bg-gray-50",
                        entering && "animate-timeline-enter"
                    )}
                >
                    <span className={cn("mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full", engine ? "bg-gray-100 text-gray-500" : visual.header, !engine && visual.iconColor)}>
                        <Icon className="h-3.5 w-3.5" aria-hidden />
                    </span>
                    <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-3">
                            <SimpleTooltip message={utcTooltip(entry.at)}>
                                <span className="shrink-0 font-mono text-xs text-gray-500">{formatParis(entry.at_local ?? entry.at, "HH:mm")}</span>
                            </SimpleTooltip>
                            <span className="min-w-0 flex-1 text-sm text-black">{entryTitle(entry)}</span>
                            {(entry.node_label || entry.node_id) && (
                                <span
                                    className={cn(
                                        "hidden max-w-64 shrink-0 items-center gap-1 rounded-sm px-2 py-0.5 text-xs font-medium sm:flex",
                                        engine ? "bg-gray-100 text-gray-600" : visual.header,
                                        !engine && visual.iconColor
                                    )}
                                >
                                    <CategoryIcon className="h-3 w-3 shrink-0" aria-hidden />
                                    <span className="truncate">{entry.node_label || entry.node_id}</span>
                                </span>
                            )}
                            {details.length > 0 && (
                                <button
                                    type="button"
                                    className="shrink-0 rounded-sm p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                                    aria-label={expanded ? "Hide event details" : "Show event details"}
                                    aria-expanded={expanded}
                                    onClick={event => {
                                        event.stopPropagation();
                                        setExpanded(value => !value);
                                    }}
                                >
                                    <ChevronDown className={cn("h-4 w-4 transition-transform", expanded && "rotate-180")} />
                                </button>
                            )}
                        </div>
                        {expanded && details.length > 0 && (
                            <dl className="mt-2 border-t pt-2 text-sm">
                                {details.map((detail, index) => (
                                    <div key={`${detail.label}-${index}`} className="grid grid-cols-[9rem_1fr] gap-2 py-0.5">
                                        <dt className="mt-0.5 text-xs text-gray-600">{detail.label}</dt>
                                        <dd className="break-words">
                                            {detail.href ? (
                                                <Link to={detail.href} className="text-blue-700 hover:text-blue-900 hover:underline" onClick={event => event.stopPropagation()}>
                                                    {detail.value}
                                                </Link>
                                            ) : (
                                                detail.value
                                            )}
                                        </dd>
                                    </div>
                                ))}
                            </dl>
                        )}
                    </div>
                </div>
            </div>
        </li>
    );
}
